import { useContext } from 'react';
import React from 'react';
import { LanguageContext } from '../context/LanguageContext';
import { getTranslation, TranslationKey } from '../utils/translations';

/**
 * Custom hook to access translations based on the current language
 * @returns An object with translation function
 */
export const useTranslation = () => {
  const context = useContext(LanguageContext);
  
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  
  const { language } = context;
  
  /**
   * Get a translation by key
   * @param key The translation key to look up
   * @returns The translated text
   */
  const t = (key: TranslationKey): string => {
    return getTranslation(key, language);
  };

  /**
   * Get a translation that may contain newlines and return JSX
   * @param key The translation key to look up
   * @param className Optional CSS class to apply to paragraph elements
   * @returns JSX element(s) for the translated text
   */
  const tJsx = (key: TranslationKey, className?: string): React.ReactNode => {
    const text = getTranslation(key, language);
    
    // If the text contains newlines, split it and render each paragraph separately
    if (text.includes('\n')) {
      return React.createElement(
        React.Fragment,
        null,
        ...text.split('\n')
          .filter((paragraph: string) => paragraph.trim() !== '')
          .map((paragraph: string, index: number) => 
            React.createElement('p', { key: index, className }, paragraph.trim())
          )
      );
    }
    
    // Otherwise, return as a single piece of text
    return text;
  };
  
  return { t, tJsx, language };
}; 